import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { EmptyState, ErrorState, Loading, NoAccess } from "../../../components/States";
import { Dialog, PageHead, PillView, Toolbar, useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { date, time } from "../../../lib/format";
import { useViewerZone } from "../../../lib/zeitzone";
import { erneutSenden, ladePostausgang, verwerfen, zurueckgehalteneSenden } from "./daten";
import {
  FILTERS, anlassMitDetail, belegText, empfaengerArt, erklaerung, istProblem, matchesFilter, pruefeAdresse, searchText, statusPill,
  type OutboxFilter, type OutboxRow,
} from "./model";
import "./postausgang.css";
import { t, ta } from "../../../lib/sprache";

/**
 * Postausgang (Befund 3): alle Mails der Agentur der letzten sechs Monate mit Empfänger, Anlass, Stand, Fehler und Zeit.
 * 0056: Probleme sind nur echte Fehler, Rückläufer und fehlende Adressen. „Erneut senden“ gibt es nur, wo es fachlich
 * stimmt (die Datenbank prüft es noch einmal) und fragt nach der Adresse, die sich dabei korrigieren lässt, auf Wunsch
 * auch im Profil des Creators oder der Marke. Bewusst übersprungene Mails stehen unter „Bewusst übersprungen“.
 * Nur für Admin (outbox_list, outbox_retry, outbox_cancel prüfen das). Sprungziel aus der Glocke: ?auftrag=<id>.
 */
export function Postausgang() {
  const ws = useStaffWorkspace();
  if (!ws.isChef) return <NoAccess text={t("Den Postausgang sehen nur Admins.", "Only admins can see the outbox.")} />;
  return <PostausgangSeite orgId={ws.org.org_id} orgZone={ws.org.timezone} />;
}

function PostausgangSeite({ orgId, orgZone }: { orgId: string; orgZone: string }) {
  const zone = useViewerZone(orgZone);
  const [params] = useSearchParams();
  const auftrag = params.get("auftrag");
  const q = useQuery({ queryKey: ["outbox", orgId, "postausgang"], queryFn: () => ladePostausgang(api, orgId) });
  const [filter, setFilter] = useState<OutboxFilter>(auftrag ? "alle" : "probleme");
  const [search, setSearch] = useState("");
  const [retry, setRetry] = useState<OutboxRow | null>(null);
  const { busy, run } = useAction();
  const rows = q.data?.rows ?? [];
  const probleme = rows.filter((r) => istProblem(r)).length;
  // 0253 (K-201): wegen unbestätigter Adresse zurückgehaltene Mails; nach der Bestätigung lassen sie sich gesammelt senden
  const zurueck = rows.filter((r) => r.status === "skipped" && r.skip_reason === "unverified");
  const zurueckSendbar = zurueck.filter((r) => r.retryable).length;
  // Ohne Probleme zeigt die Seite gleich alles (sonst stünde dort eine leere Liste)
  const effektiv: OutboxFilter = filter === "probleme" && probleme === 0 && !search ? "alle" : filter;
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => matchesFilter(r, effektiv) && (!s || searchText(r).includes(s)));
  }, [rows, effektiv, search]);
  const ziel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (auftrag && ziel.current) ziel.current.scrollIntoView({ block: "center" });
  }, [auftrag, q.data]);

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Loading />;
  const sum = q.data.summary;
  const zahlen = [
    t(`${sum.sent} versendet`, `${sum.sent} sent`), sum.pending ? t(`${sum.pending} wartend`, `${sum.pending} pending`) : null, sum.held ? t(`${sum.held} für die Tagesübersicht`, `${sum.held} for the daily summary`) : null,
    sum.failed ? t(`${sum.failed} gescheitert`, `${sum.failed} failed`) : null, sum.bounced ? t(`${sum.bounced} zurückgekommen`, `${sum.bounced} bounced`) : null,
    sum.no_address ? t(`${sum.no_address} ohne Adresse`, `${sum.no_address} without address`) : null,
    sum.skipped - (sum.no_address ?? 0) > 0 ? t(`${sum.skipped - (sum.no_address ?? 0)} bewusst übersprungen`, `${sum.skipped - (sum.no_address ?? 0)} deliberately skipped`) : null,
    sum.dry_run ? t(`${sum.dry_run} im Probelauf`, `${sum.dry_run} in dry run`) : null,
  ].filter(Boolean).join(" · ");

  return (
    <main id="main" className="content tk-postausgang">
      <PageHead eyebrow={ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")} title={t("Postausgang", "Outbox")}
        text={ta(`Alle Mails der Agentur an Marken, Creator und das Team. Letzte 30 Tage: ${zahlen}.`, `All agency emails to brands, creators and the team. Last 30 days: ${zahlen}.`, `Alle deine Mails an Marken. Letzte 30 Tage: ${zahlen}.`, `All your emails to brands. Last 30 days: ${zahlen}.`)}>
        <button type="button" onClick={() => void q.refetch()} disabled={q.isFetching}>{t("Aktualisieren", "Refresh")}</button>
      </PageHead>
      {probleme > 0 && (
        <div className="notice" role="status">
          {probleme === 1 ? t("1 Mail ist", "1 email has") : t(`${probleme} Mails sind`, `${probleme} emails have`)} {t("nicht angekommen: gescheitert, zurückgekommen oder ohne Adresse. Prüfe die Adresse und sende erneut. Gescheiterte Mails zu Rechnungen stehen auch im Verlauf der Rechnung.", "not arrived: failed, bounced or without address. Check the address and send again. Failed emails about invoices also appear in the invoice history.")}</div>
      )}
      {zurueck.length > 0 && (
        <div className="notice row" role="status">
          <span>{zurueckSendbar > 0
            ? t(`${zurueck.length === 1 ? "1 Mail wurde" : `${zurueck.length} Mails wurden`} zurückgehalten, solange deine E-Mail-Adresse unbestätigt war. Die Adresse ist jetzt bestätigt.`, `${zurueck.length === 1 ? "1 email was" : `${zurueck.length} emails were`} held while your email address was not confirmed. The address is confirmed now.`)
            : t(`${zurueck.length === 1 ? "1 Mail wird" : `${zurueck.length} Mails werden`} zurückgehalten, bis du deine E-Mail-Adresse bestätigst (Hinweis oben auf der Seite).`, `${zurueck.length === 1 ? "1 email is" : `${zurueck.length} emails are`} held until you confirm your email address (notice at the top of the page).`)}</span>
          {zurueckSendbar > 0 && (
            <button type="button" className="primary" data-schreiben disabled={busy !== null}
              onClick={() => void run("zurueck", async () => {
                const r = await zurueckgehalteneSenden(api, orgId);
                await q.refetch();
                toast(r.liegen > 0
                  ? t(`${r.erneut} erneut eingereiht. ${r.liegen} bleiben liegen, weil ihr Anlass erledigt ist.`, `${r.erneut} queued again. ${r.liegen} stay, because their occasion is settled.`)
                  : t(`${r.erneut} erneut eingereiht.`, `${r.erneut} queued again.`));
              })}>
              {t("Zurückgehaltene Mails jetzt senden", "Send held emails now")}
            </button>
          )}
        </div>
      )}
      <Toolbar search={search} onSearch={setSearch} label={t("Postausgang durchsuchen", "Search outbox")} placeholder={t("Empfänger, Anlass, Rechnung …", "Recipient, occasion, invoice …")}
        filters={FILTERS.map((f) => ({ ...f, count: f.key === "probleme" ? probleme : undefined }))} filter={effektiv} onFilter={setFilter} />
      {shown.length === 0 ? (
        <EmptyState title={rows.length ? t("Keine passenden Mails.", "No matching emails.") : t("Noch keine Mails.", "No emails yet.")}
          text={rows.length ? t("Passe die Suche oder den Filter an.", "Adjust the search or the filter.") : ta("Sobald die Agentur Mails verschickt, stehen sie hier mit ihrem Stand.", "As soon as the agency sends emails, they appear here with their status.", "Sobald du Mails verschickst, stehen sie hier mit ihrem Stand.", "As soon as you send emails, they appear here with their status.")} />
      ) : (
        <section className="panel stack" aria-label={t("Mails", "Emails")}>
          {shown.map((r) => (
            <Zeile key={r.id} r={r} zone={zone} busy={busy !== null} markiert={r.id === auftrag} refMark={r.id === auftrag ? ziel : undefined}
              onRetry={() => setRetry(r)}
              onCancel={() => void run("cancel", () => verwerfen(api, r.id), t("Mail verworfen.", "Email discarded."))} />
          ))}
        </section>
      )}
      <Dialog open={!!retry} onClose={() => setRetry(null)} title={t("Erneut senden", "Send again")}>
        {retry && <ErneutSenden key={retry.id} r={retry} onDone={() => setRetry(null)} />}
      </Dialog>
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Es wird keine Mail verschickt. Alle Daten sind erfunden.", "Demo · No email is sent. All data is fictional.")}</p>}
    </main>
  );
}

/** Adresse prüfen oder korrigieren und die Mail wieder einreihen (Befunde 1 und 3). */
function ErneutSenden({ r, onDone }: { r: OutboxRow; onDone: () => void }) {
  const { busy, error, run } = useAction();
  const start = r.suggested_recipient ?? r.recipient ?? "";
  const [adresse, setAdresse] = useState(start);
  const [profil, setProfil] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const geaendert = adresse.trim().toLowerCase() !== (r.recipient ?? "").trim().toLowerCase();
  const profilText = r.master_kind === "talent" ? t("Adresse auch im Creator-Profil speichern", "Also save the address in the creator profile") : r.master_kind === "brand" ? t("Adresse auch als Rechnungsadresse der Marke speichern", "Also save the address as the brand's billing address") : null;
  async function submit(e: FormEvent) {
    e.preventDefault();
    const f = pruefeAdresse(adresse) ?? (!adresse.trim() && !r.recipient ? t("Bitte eine Adresse eintragen.", "Please enter an address.") : null);
    setFehler(f);
    if (f) return;
    const res = await run("retry", () => erneutSenden(api, r.id, { recipient: geaendert ? adresse : null, updateMaster: profil && geaendert }));
    if (!res) return;
    if (res.status === "pending") {
      toast(t(`Mail an ${res.recipient ?? adresse} eingereiht. Sie geht mit dem nächsten Versandlauf raus.${res.master ? t(" Die Adresse ist im Profil gespeichert.", " The address has been saved in the profile.") : ""}`, `Email to ${res.recipient ?? adresse} queued. It goes out with the next sending run.${res.master ? t(" Die Adresse ist im Profil gespeichert.", " The address has been saved in the profile.") : ""}`));
      onDone();
    } else {
      toast(t(`Die Mail ist weiterhin nicht versendbar: ${res.error ?? t("unbekannter Fehler", "unknown error")}`, `The email still cannot be sent: ${res.error ?? t("unbekannter Fehler", "unknown error")}`), "alert");
    }
  }
  return (
    <form className="stack" onSubmit={submit} noValidate>
      <p><strong>{anlassMitDetail(r)}</strong>{r.talent_name ? ` · ${r.talent_name}` : ""}{belegText(r) ? ` · ${belegText(r)}` : ""}</p>
      {r.last_error && <p className="muted">{t("Bisher: ", "Previously: ")}{r.recipient || t("keine Adresse", "no address")} · {r.last_error}</p>}
      <label>
        {t("Empfänger", "Recipient")}<input type="email" value={adresse} onChange={(e) => setAdresse(e.target.value)} autoComplete="off" aria-invalid={fehler ? true : undefined}
          aria-describedby="retry-hinweis" required />
        <small id="retry-hinweis">
          {r.suggested_recipient && r.suggested_recipient !== r.recipient ? t(`Im Profil steht heute ${r.suggested_recipient}. `, `The profile currently shows ${r.suggested_recipient}. `) : ""}
          {t("Bei Tippfehlern oder neuer Buchhaltungsadresse hier korrigieren.", "Correct it here in case of typos or a new accounting address.")}</small>
      </label>
      {profilText && geaendert && (
        <label className="row"><input type="checkbox" checked={profil} onChange={(e) => setProfil(e.target.checked)} />{profilText}</label>
      )}
      {(fehler || error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}
      <div className="row">
        <button type="submit" className="primary" disabled={busy !== null}>{busy ? t("Wird eingereiht …", "Queuing …") : t("Erneut senden", "Send again")}</button>
        <button type="button" onClick={onDone}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </form>
  );
}

function Zeile({ r, zone, busy, markiert, refMark, onRetry, onCancel }: {
  r: OutboxRow; zone: string; busy: boolean; markiert: boolean; refMark?: React.RefObject<HTMLDivElement | null>;
  onRetry: () => void; onCancel: () => void;
}) {
  const wann = r.delivery_at ?? r.sent_at ?? r.failed_at ?? r.created_at;
  const bezug = [belegText(r), r.talent_name].filter(Boolean).join(" · ");
  const hinweis = erklaerung(r);
  const problem = istProblem(r);
  return (
    <div ref={refMark} className={`task tk-outbox${markiert ? " tk-outbox-mark" : ""}`} aria-current={markiert ? "true" : undefined}>
      <div className="tk-outbox-main">
        <strong>{anlassMitDetail(r)}</strong>
        <p>
          {empfaengerArt(r.recipient_kind, r.text_key)}: <span className="tk-outbox-mail">{r.recipient || t("keine Adresse", "no address")}</span>
          {bezug ? ` · ${bezug}` : ""} · {date(wann, zone)} {time(wann, zone)}
        </p>
        {hinweis && <p className={problem ? "tk-error" : "muted"}>{hinweis}</p>}
        <div className="row">
          {/* Nur-Lesen-Phase (KETTE-8): der Knopf verschwindet wie jeder Schreibknopf; die Datenbank sperrt ohnehin */}
          {/* D-2: Zeilenaktion als ruhiger Knopf; gefüllt bleibt die Hauptaktion oben */}
          {r.retryable && <button type="button" data-schreiben disabled={busy} onClick={onRetry}>{t("Erneut senden", "Send again")}</button>}
          {(r.status === "pending" || r.status === "held") && <button type="button" disabled={busy} onClick={onCancel}>{t("Verwerfen", "Discard")}</button>}
          {r.invoice_id && <Link className="btn" to={`/agentur/rechnungen/${r.invoice_id}`}>{t("Zur Rechnung", "Go to invoice")}</Link>}
          {!r.invoice_id && r.talent_id && <Link className="btn" to={`/agentur/creator/${r.talent_id}`}>{t("Zum Creator", "Go to creator")}</Link>}
        </div>
      </div>
      <PillView pill={statusPill(r)} />
    </div>
  );
}
